import type { ConfigTreeNode, ConfigurationLevel } from '../types';

/** Config keys whose UI value is 2× API value; user input is halved when sending to API. */
export const MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS = [
  'shrink.min_size_bytes',
  'datafy.min_size_bytes',
] as const;

/**
 * Recursively search for a node in the tree by ID
 */
export const findNodeInTree = (
  tree: ConfigTreeNode | null,
  nodeId: string
): ConfigTreeNode | null => {
  if (!tree) {
    return null;
  }

  const search = (node: ConfigTreeNode): ConfigTreeNode | null => {
    if (node.id === nodeId) {
      return node;
    }
    if (node.children) {
      for (const child of node.children) {
        const found = search(child);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  return search(tree);
};

// Format field names for display (e.g., "shrink.threshold_abort" -> "Shrink Threshold Abort")
export const formatFieldDisplayName = (fieldName: string): string => {
  return fieldName.replace(/[._]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export function formatConfigurationLevelLabel(level: ConfigurationLevel | string): string {
  if (level === 'org') {
    return 'organization';
  }
  return level;
}

export const transformErrorMessage = (errorMessage: string): string => {
  return errorMessage.replace(/parameter\s+([a-z_][a-z0-9_.]*):/gi, (match, fieldName) => {
    const formattedFieldName = formatFieldDisplayName(fieldName);
    return match.replace(fieldName, formattedFieldName);
  });
};

export const INSTANCE_FAMILY_LEVELS = ['instance', 'node', 'volume'] as const;

export function isInstanceFamilyLevel(level?: ConfigurationLevel): boolean {
  return level === 'instance' || level === 'node' || level === 'volume';
}

export function getEffectiveInstanceFamilyId(
  level?: ConfigurationLevel,
  nodeId?: string | null,
  instanceId?: string
): string | undefined {
  if (level === 'node' || level === 'volume') {
    return nodeId ?? instanceId ?? undefined;
  }
  return instanceId;
}

export function getInstanceFamilyErrorMessage(level?: ConfigurationLevel): string {
  switch (level) {
    case 'instance':
      return 'instanceId is required for instance level configurations';
    case 'node':
      return 'node ID is required for node level configurations';
    case 'volume':
      return 'volume ID is required for volume level configurations';
    default:
      return 'id is required for instance-family level configurations';
  }
}

export interface ConfigurationScopeInput {
  level: ConfigurationLevel;
  accountId?: string;
  clusterId?: string;
  nodeId?: string | null;
  instanceId?: string;
}

export interface ResolvedConfigurationScope {
  accountId?: string;
  clusterId?: string;
  instanceId?: string;
}

/** Derives API scope fields (accountId, clusterId, instanceId) from level and node context. */
export function buildConfigurationScope({
  level,
  accountId,
  clusterId,
  nodeId,
  instanceId,
}: ConfigurationScopeInput): ResolvedConfigurationScope {
  const scope: ResolvedConfigurationScope = {};
  const isInstanceFamily = isInstanceFamilyLevel(level);
  const effectiveInstanceFamilyId = getEffectiveInstanceFamilyId(level, nodeId, instanceId);

  if ((level === 'account' || level === 'cluster' || isInstanceFamily) && accountId) {
    scope.accountId = accountId;
  }

  if ((level === 'cluster' || isInstanceFamily) && clusterId) {
    scope.clusterId = clusterId;
  }

  if (isInstanceFamily && effectiveInstanceFamilyId) {
    scope.instanceId = effectiveInstanceFamilyId;
  }

  return scope;
}
